import { Injectable, signal } from '@angular/core';
import { collectionGroup, getDocs } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { NewsModel } from '../models/news.model';
import { ImageModel } from '../models/image.model';
import { JobModel } from '../models/job.model';
import { ProductModel } from '../models/product.model';
import { VideoModel } from '../models/video.model';

@Injectable({
  providedIn: 'root'
})
export class SearchDataService {
  loading = signal<boolean>(false);
  error = signal<string | null>(null);

  /**
   * Helper utility to parse multi-format Firestore timestamps (Timestamp, Date, String, Number).
   * Returns a Unix timestamp (seconds).
   */
  private getTimestampSeconds(timestamp: any): number {
    if (!timestamp) return 0;
    if (timestamp && typeof timestamp === 'object') {
      if ('toMillis' in timestamp && typeof timestamp.toMillis === 'function') {
        return timestamp.toMillis() / 1000;
      }
      if ('seconds' in timestamp) {
        return timestamp.seconds;
      }
      if (timestamp instanceof Date) {
        return timestamp.getTime() / 1000;
      }
    }
    const parsed = new Date(timestamp);
    return isNaN(parsed.getTime()) ? 0 : parsed.getTime() / 1000;
  }

  /**
   * Fetches sub-collection documents across ALL users using Collection Group queries,
   * performs strict verificationStatus and status validation, maps results to respective models,
   * and sorts results in descending order by timestamp.
   */
  async fetchCollectionGroupData<T extends { id: string; ownerUid: string; status?: string; verificationStatus?: string; createdAt?: any; timestamp?: any }>(
    subCollection: 'news' | 'images' | 'jobs' | 'products' | 'videos'
  ): Promise<T[]> {
    this.loading.set(true);
    this.error.set(null);
    const path = `accounts/*/${subCollection}`;
    
    try {
      const q = collectionGroup(db, subCollection);
      const querySnapshot = await getDocs(q);
      
      const results: T[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data() as any;
        
        const content = data.content || data.description || '';
        const description = data.description || data.content || '';
        let bannerImage = data.bannerImage || data.imageUrl || '';
        if (Array.isArray(bannerImage)) {
          bannerImage = bannerImage[0] || '';
        }
        if (!bannerImage && Array.isArray(data.images) && data.images.length > 0) {
          bannerImage = data.images[0] || '';
        }
        if (!bannerImage && Array.isArray(data.galleryImages) && data.galleryImages.length > 0) {
          bannerImage = data.galleryImages[0] || '';
        }

        // Form the mapped model object
        const mappedItem = {
          ...data,
          content,
          description,
          bannerImage,
          id: doc.id,
          // Extract the parent userId from the document reference path
          ownerUid: doc.ref.parent.parent?.id || data.ownerUid || '',
        } as T;

        // Core algorithm validation conditions:
        // status must be strictly 'published' / public
        // verificationStatus must be strictly 'approved'
        const isPublic = data['visibility'] === 'public';
        const isApproved = mappedItem.status === 'approved' || mappedItem.verificationStatus === 'approved' || mappedItem.status === 'published';

        if (subCollection === 'images') {
          if (isPublic && isApproved) {
            results.push(mappedItem);
          }
        } else {
          if ((isPublic || mappedItem.status === 'published') && isApproved) {
            results.push(mappedItem);
          }
        }
      });

      // Sort in descending order to show latest items first
      results.sort((a, b) => {
        const timeA = this.getTimestampSeconds(a.createdAt || a.timestamp);
        const timeB = this.getTimestampSeconds(b.createdAt || b.timestamp);
        return timeB - timeA;
      });

      return results;
    } catch (err) {
      handleFirestoreError(err, OperationType.GET, path);
      return [];
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Evaluates the active tab category name and fetches the corresponding
   * verified, filtered, and sorted custom list results.
   */
  async getTabData(tabName: string): Promise<any[]> {
    const normalized = tabName.toLowerCase();
    switch (normalized) {
      case 'news':
        return this.fetchCollectionGroupData<NewsModel>('news');
      case 'images':
        return this.fetchCollectionGroupData<ImageModel>('images');
      case 'jobs':
        return this.fetchCollectionGroupData<JobModel>('jobs');
      case 'products':
        return this.fetchCollectionGroupData<ProductModel>('products');
      case 'videos':
        return this.fetchCollectionGroupData<VideoModel>('videos');
      default:
        return [];
    }
  }
}
